"use client"

import { ProductCategory } from "@medusajs/medusa"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { twMerge } from "tailwind-merge"

import { isRoute } from "@lib/routes"
import ClientEventLogger from "@lib/util/client-event-logger"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import TopBanner from "@modules/layout/components/top-banner"
import { MobileCategoriesButton } from "@modules/navigation/components/mobile-categories-button"
import MobileCategoriesContent from "@modules/navigation/components/mobile-categories-content"
import UserProfileDropdown from "@modules/navigation/components/user-profile-dropdown"
import { useMobileCategories } from "@modules/navigation/hooks/use-mobile-categories"
import { useMobileSearch } from "@modules/navigation/hooks/use-mobile-search"
import { CancelIcon } from "@modules/navigation/icons/CancelIcon"
import { HamburgerIcon } from "@modules/navigation/icons/hamburger"
import { MobileSearch } from "@modules/nelo-search/components/mobile-search"
import { useClientInitialization } from "@modules/signup-and-order-flow/util/use-client-initializations"
import { useGetBootstrap } from "@modules/signup-and-order-flow/util/useGetBootstrap"
import { useGetProfile } from "@modules/signup-and-order-flow/util/useGetProfile"

import { useScrollY } from "../navbar/useScrollY"

type NavbarMobileProps = {
  categories: ProductCategory[]
  cartButton: JSX.Element
  rating: number
  showTopBanner: boolean
}

export default function NavbarMobile(props: NavbarMobileProps) {
  const { scrollY } = useScrollY()
  const pathname = usePathname()

  const isCategoryPage = isRoute.categoryOrCollection(pathname)

  return (
    <div
      className={twMerge("z-50 bg-white shadow-none transition-shadow", isCategoryPage ? "" : scrollY !== 0 && "sticky top-0 shadow-2xl")}
    >
      {props.showTopBanner && <TopBanner rating={props.rating} />}

      <header className="relative mx-auto bg-white text-black duration-200">
        <NavbarMobileContent hideSearchBar={isCategoryPage} scrollY={scrollY} {...props} />
      </header>
    </div>
  )
}

interface NavbarMobileContentProps extends NavbarMobileProps {
  scrollY: number
  hideSearchBar?: boolean
}

const NavbarMobileContent = ({ cartButton, categories, scrollY, hideSearchBar }: NavbarMobileContentProps) => {
  const { showMobileCategories, toggleMobileCategories } = useMobileCategories()
  const { dismissMobileSearch } = useMobileSearch()

  // Show search bar on medium screens always, or on small screens when scrolled
  const showSearchBarOnSmall = !hideSearchBar && scrollY > 10

  const { isInitialized } = useClientInitialization()
  const { profile, isSuccess } = useGetProfile(isInitialized)
  const { data: bootstrapData, isSuccess: isBootstrapSuccess } = useGetBootstrap(isInitialized)

  const renderUserprofileDropdown = isInitialized && isSuccess && profile && isBootstrapSuccess && bootstrapData

  return (
    <div className="flex flex-col">
      <ClientEventLogger
        name="VIEW_NAVBAR"
        params={{
          isMobileView: true,
          showMobileCategories,
          showSearchBarOnSmall,
        }}
      />
      {/* First row: Hamburger, Logo, Cart */}
      <div className="content-container flex h-16 items-center justify-between">
        <MobileCategoriesButton onClick={toggleMobileCategories}>
          {showMobileCategories ? <CancelIcon className="w-6" /> : <HamburgerIcon className="w-6" />}
        </MobileCategoriesButton>

        <LocalizedClientLink href="/" data-testid="nav-store-link" className={renderUserprofileDropdown ? "ml-14" : ""}>
          <Image src="/images/wordmark-blue.svg" className="h-7 object-contain" priority height={32} width={128} alt="Nelo" />
        </LocalizedClientLink>

        <div className="flex items-center justify-center gap-2">
          {cartButton}
          {renderUserprofileDropdown && (
            <UserProfileDropdown
              profile={profile}
              availableCreditFormatted={bootstrapData.creditBalance.marketplace.available.displayValue}
              color="nelo-black"
              variant="contained"
            />
          )}
        </div>
      </div>

      {/* Search bar container with properly organized responsive classes */}
      <div
        className={twMerge(
          "content-container mx-auto overflow-visible transition-all duration-100",
          "h-0 opacity-0",
          "lg:hidden",
          hideSearchBar && "hidden",
          !hideSearchBar && "md:mb-3 md:mt-1 md:h-12 md:opacity-100",
          showSearchBarOnSmall && "mb-3 mt-1 h-12 opacity-100"
        )}
        style={{ transitionProperty: "height, opacity, margin, padding" }}
      >
        <MobileSearch dismissSearchInput={dismissMobileSearch} />
      </div>

      {/* Mobile Categories Menu */}
      {showMobileCategories && <MobileCategoriesContent categories={categories} />}
    </div>
  )
}
